import styles from "./AgendaWeekChooser.module.css";
import Settings02Icon from "../../main/icons/settings-02-stroke-rounded.tsx";
import MainMenu from "../../main/components/MainMenu.tsx";
import { skipToken } from "@tanstack/react-query";
import RdvsLoading from "../../../components/RdvsLoading.tsx";
import ArrowRight01Icon from "../../main/icons/arrow-right-01-stroke-rounded.tsx";
import ArrowLeft01Icon from "../../main/icons/arrow-left-01-stroke-rounded.tsx";
import DoctorChooser from "../../../components/DoctorChooser.tsx";
import { useDroppable } from "@dnd-kit/core";
import StickiesOrigin from "../../sticky-notes/StickiesOrigin.tsx";
import { trpc } from "../../main/components/trpc.ts";
import { FaArrowDown, FaArrowUp, FaMinus } from "react-icons/fa";
import useOwner from "../../users/hooks/useOwner.ts";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface AgendaWeekChooserProps {
  year: number;
  weekNumber: number;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onThisWeek: () => void;
  user: { fullname: string; tag: "doctor" | "secretary" | "app" } | null;
}

const AgendaWeekChooser = (props: AgendaWeekChooserProps) => {
  const { year, weekNumber, onNextWeek, onPrevWeek, onThisWeek } = props;
  const { setNodeRef } = useDroppable({ id: "weekChooser" });
  const { setNodeRef: setMainSettingsButtonNodeRef } = useDroppable({
    id: "agendaMainSettingsButton",
  });
  const owner = useOwner();
  const trpcUtils = trpc.useUtils();
  const { data: aptFlowSlope } = trpc.stats.getAptFlowDirection.useQuery(
    owner ? undefined : skipToken,
    { staleTime: 1000 * 60 * 5 },
  );
  const respondHomeQuery = trpc.respond.getHomeForOwner.useQuery(
    owner ? { limit: 1 } : skipToken,
    { staleTime: 1000 * 60 },
  );
  const selectAnnounceSlotMutation =
    trpc.respond.selectAnnounceSlotForOwner.useMutation({
      onSuccess: () => {
        trpcUtils.respond.getHomeForOwner.invalidate();
      },
    });
  const anchorStyle = {
    anchorName: "--weekChooser",
  };
  const mainSettingsAnchorStyle = {
    anchorName: "--agendaMainSettingsButton",
  };

  const aptFlowIcon = (() => {
    if (aptFlowSlope === null || aptFlowSlope === undefined) return null;
    if (aptFlowSlope > 0.001) {
      return (
        <span
          className={`${styles.aptFlowIcon} ${styles.aptFlowUp}`}
          title={`Prises de RDV en marée montante (${aptFlowSlope.toFixed(2)})`}
        >
          <FaArrowUp />
        </span>
      );
    }
    if (aptFlowSlope < -0.001) {
      return (
        <span
          className={`${styles.aptFlowIcon} ${styles.aptFlowDown}`}
          title={`Prises de RDV en marée descendante (${aptFlowSlope.toFixed(2)})`}
        >
          <FaArrowDown />
        </span>
      );
    }
    return (
      <span
        className={`${styles.aptFlowIcon} ${styles.aptFlowStable}`}
        title={`Prises de RDV stables (${aptFlowSlope.toFixed(2)})`}
      >
        <FaMinus />
      </span>
    );
  })();

  const announceSettings = respondHomeQuery.data?.announceSettings;
  const announceNamesInSlots = announceSettings
    ? [
        announceSettings.slot_one,
        announceSettings.slot_two,
        announceSettings.slot_three,
      ].map(
        (announceId) =>
          respondHomeQuery.data?.announces.find(
            (announce) => announce.id === announceId,
          )?.name || null,
      )
    : [];
  const compactAnnounceSlots: Array<1 | 2 | 3> = [1, 2, 3];
  const hasVisibleAnnounceShortcut = announceNamesInSlots.some(Boolean);

  return (
    <>
      <div
        id="weekChooser"
        className={styles.mainSpacerStyle}
        ref={setNodeRef}
        //@ts-ignore anchorStyle pas encore dans le d.ts
        style={anchorStyle}
      >
        <div className={styles.spacer}>
          <Button
            id="button_prev_week"
            className="text-white"
            variant="ghost"
            size="icon"
            onClick={onPrevWeek}
          >
            <ArrowLeft01Icon color="white" />
          </Button>
          <Button
            id="button_next_week"
            className="text-white"
            variant="ghost"
            size="icon"
            onClick={onNextWeek}
          >
            <ArrowRight01Icon color="white" />
          </Button>
          <Button
            id="button_today"
            className="text-white"
            variant="outline"
            onClick={onThisWeek}
          >
            Cette Semaine
          </Button>
          <div className={styles.weekNumberStyle}>
            <span id="year_display">{year} </span>
            Semaine
            <span id="week_number_display"> {weekNumber}</span>
            {aptFlowIcon}
          </div>
          {hasVisibleAnnounceShortcut ? (
            <div className={styles.announceShortcutGroup}>
              {compactAnnounceSlots.map((slot) => {
                const announceName = announceNamesInSlots[slot - 1];
                const label = announceName
                  ? `Activer l'annonce ${announceName}`
                  : `Slot ${slot} vide`;
                const isSelected = announceSettings?.selected_slot === slot;
                const initial = announceName?.trim().charAt(0).toUpperCase() || "-";

                return (
                  <button
                    key={`agenda-announce-slot-${slot}`}
                    type="button"
                    className={`${styles.announceShortcutButton} ${isSelected ? styles.announceShortcutButtonSelected : ""}`}
                    title={announceName || label}
                    aria-label={label}
                    disabled={!announceName || selectAnnounceSlotMutation.isPending}
                    onClick={() => {
                      selectAnnounceSlotMutation.mutate({ slot });
                    }}
                  >
                    {initial}
                  </button>
                );
              })}
            </div>
          ) : null}
          {props.user?.tag === "secretary" ? <DoctorChooser /> : null}
          <StickiesOrigin weekSelected={weekNumber} yearSelected={year} />
          <RdvsLoading />
        </div>
        <div className={styles.spacer}>
          <div className={styles.userStyle} data-testid="userDisplay">
            {props.user?.fullname}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  id="agendaMainSettingsButton"
                  className="text-white"
                  data-testid="mainSettingsButton"
                  variant="outline"
                  size="icon"
                  ref={setMainSettingsButtonNodeRef}
                  //@ts-ignore anchorStyle pas encore dans le d.ts
                  style={mainSettingsAnchorStyle}
                />
              }
            >
              <Settings02Icon color="white" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <MainMenu />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </>
  );
};

export default AgendaWeekChooser;
